"use client";

import { Button } from "@/components/atoms/Button";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import Image from "next/image";
import { useRef } from "react";
import { useDispatch } from "react-redux";

interface AcademicDeliverablesData {
  title: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  desktop_bg_image: string;
  mobile_bg_image: string;
  alt_image?: string;
  button_text?: string | null;
  button_text_color?: string | null;
  button_background_color?: string | null;
  button_icon?: string | null;
  button_icon_alt?: string | null;
}

const AcademicDeliverables1 = ({ data }: { data: any }) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile(800);
  const ref = useRef<HTMLElement>(null);

  // Track widget viewed - must be called before any early returns
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "academic_deliverables_section");
  //   }
  // }, []);

  // Handle both single and double nesting from PreviewRenderer
  const sectionData = (data as any)?.data || data;

  if (!sectionData || !sectionData?.status) {
    return null;
  }

  const {
    title = "",
    title_text_color = "#000000",
    sub_title = "",
    sub_title_text_color = "#000000",
    desktop_bg_image = "",
    mobile_bg_image = "",
    alt_image = "",
    button_text,
    button_text_color,
    button_background_color,
    button_icon,
    button_icon_alt,
  } = sectionData || {};

  // Handle null values with nullish coalescing
  const btnText = button_text ?? "";
  const btnTextColor = button_text_color ?? "#FFFFFF";
  const btnBgColor = button_background_color ?? "#BE2330";
  const btnIcon = button_icon ?? "";
  const btnIconAlt = button_icon_alt ?? "";

  // Choose image based on device
  const imageUrl = isMobile
    ? mobile_bg_image || desktop_bg_image
    : desktop_bg_image;

  return (
    <section
      ref={ref}
      className={cn(
        " bg-white  w-full",
        "px-5 py-spacing-6 ",
        "sm:px-0 sm:py-11",
        "flex items-center justify-center",
      )}
    >
      <div
        className={cn(
          "flex flex-col items-center justify-center w-full",
          "gap-spacing-24",
          "sm:gap-8",
          "md:gap-spacing-32",
          "xl:gap-spacing-13",
        )}
      >
        {/* Header Section */}
        <div className="flex flex-col items-center justify-center w-full ">
          {title && (
            <p
              className={cn(
                "text-heading-6-semibold",
                "sm:text-heading-5-semibold sm:line-clamp-1 sm:px-10 ",
                "md:text-heading-4-semibold",
                "lg:text-new-heading-34-semibold",
                "xl:text-new-heading-42-semibold",
                "line-clamp-2",
                "type-h3",
              )}
              style={{ color: title_text_color }}
            >
              {title}
            </p>
          )}
          {sub_title && (
            <p
              className={cn(
                "text-body-3-regular text-center",
                "xs:line-clamp-3",
                "sm:text-body-3-regular sm:line-clamp-1 sm:w-[80%] ",
                "md:text-body-2-regular md:line-clamp-1",
                "lg:text-body-1-regular lg:line-clamp-1",
                "xl:text-new-body-24-regular xl:line-clamp-1",
                "type-bidy",
              )}
              style={{ color: sub_title_text_color }}
            >
              {sub_title}
            </p>
          )}
        </div>
        {imageUrl && (
          <div
            className={cn(
              "w-full h-full object-contain",
              "xs:h-[286px]",
              "sm:h-[260px]",
              "md:h-[262px]",
              "lg:h-[262px]",
              "xl:h-[352px]",
            )}
          >
            <Image
              fetchPriority="high"
              priority
              src={imageUrl}
              alt={alt_image}
              width={1000}
              height={1000}
              className={cn("w-full h-full object-cover")}
            />
          </div>
        )}

        {btnText && (
          <Button
            className={cn(
              "text-heading-9-semibold flex gap-[6px] ",
              "sm:text-heading-9-semibold sm:gap-[6px] sm:px-[14px] sm:py-[6px]",
              "md:text-heading-7-semibold md:gap-[6px] md:px-[16px] md:py-[6px]",
              "lg:text-heading-6-semibold lg:gap-[10px] lg:py-2 lg:px-6",
              "xl:text-heading-4-semibold xl:gap-[10px] xl:py-2 xl:px-6",
            )}
            style={{
              backgroundColor: btnBgColor,
              color: btnTextColor,
            }}
            onClick={() =>
              dispatch(
                handleOpenLeaddrop({
                  variant: LEAD_DROP_VARIANTS.REGISTER,
                }),
              )
            }
          >
            {btnText}
            {btnIcon && (
              <div
                className={cn(
                  "relative size-[18px]",
                  "sm:size-[26px]",
                  "md:size-8",
                  "lg:size-9",
                  "xl:size-9",
                )}
              >
                <Image src={btnIcon} alt={btnIconAlt} fill />
              </div>
            )}
          </Button>
        )}
      </div>
    </section>
  );
};

export default AcademicDeliverables1;
